import React, { useRef, useState } from 'react';
import {
  createUser, updateUser, resetUserPassword, regenerateAgentKey, deleteUser, resetUserMfa,
} from '../../services/api';
import PasswordInput from '../PasswordInput';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import {
  ROLES, formatDateTime, label, input, error as errorStyle, success as successStyle,
} from './userStyles';

const HUMAN_ROLES = ['ADMIN', 'MANAGER'];

/**
 * Création (user = null) ou édition d'un utilisateur (DO-208). L'identifiant n'est
 * pas modifiable. Compte robot (PUSH_AGENT) : ni email ni mot de passe, le serveur
 * génère une clé affichée une seule fois (création, régénération). Un compte ne
 * passe pas de robot à humain ni l'inverse (le back le refuse aussi).
 *
 * DO-207 : robot = adresses IP autorisées (vide = liste par défaut ROBOT_ALLOWED_IPS) ;
 * humain = état du MFA et réinitialisation (authentificateur perdu).
 */
export default function UserFormModal({ user, isMe, policy, onClose, onSaved }) {
  const isNew = !user;
  const [form, setForm] = useState({
    username: user?.username || '',
    email: user?.email || '',
    role: user?.role || 'MANAGER',
    notes: user?.notes || '',
    password: '',
    // Stockée « a, b » côté back ; une adresse par ligne à l'écran
    allowedIps: (user?.allowedIps || '').split(', ').filter(Boolean).join('\n'),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [pwdMessage, setPwdMessage] = useState(null);   // { ok, text }
  const [shownKey, setShownKey] = useState(null);       // clé générée, affichée une fois
  const [confirmRegen, setConfirmRegen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [mfa, setMfa] = useState({ enabled: !!user?.mfaEnabled, enabledAt: user?.mfaEnabledAt });
  const [confirmMfaReset, setConfirmMfaReset] = useState(false);
  const [mfaMessage, setMfaMessage] = useState(null);   // { ok, text }

  const isRobot = form.role === 'PUSH_AGENT';
  // En édition, seuls les rôles de la même famille sont proposés (robot ↔ humain interdit)
  const roleOptions = isNew
    ? Object.keys(ROLES)
    : user.role === 'PUSH_AGENT' ? ['PUSH_AGENT'] : HUMAN_ROLES;

  const set = field => e => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    if (isNew && !isRobot && form.password.length < 8) {
      setError('Le mot de passe doit faire au moins 8 caractères.');
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const created = await createUser(isRobot
          ? { username: form.username, role: form.role, notes: form.notes, allowedIps: form.allowedIps }
          : { ...form, allowedIps: null });
        onSaved();
        if (created.generatedKey) { setShownKey(created.generatedKey); return; }
      } else {
        await updateUser(user.id, {
          email: isRobot ? null : form.email, role: form.role, notes: form.notes,
          allowedIps: isRobot ? form.allowedIps : null,
        });
        onSaved();
      }
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    setPwdMessage(null);
    if (newPassword.length < 8) {
      setPwdMessage({ ok: false, text: 'Le mot de passe doit faire au moins 8 caractères.' });
      return;
    }
    try {
      await resetUserPassword(user.id, newPassword);
      setNewPassword('');
      setPwdMessage({ ok: true, text: 'Mot de passe réinitialisé ✔' });
    } catch (err) {
      setPwdMessage({ ok: false, text: err.response?.data?.error || 'Réinitialisation impossible.' });
    }
  };

  const handleRegenerate = async () => {
    setPwdMessage(null);
    try {
      const { key } = await regenerateAgentKey(user.id);
      setConfirmRegen(false);
      setShownKey(key);
    } catch (err) {
      setPwdMessage({ ok: false, text: err.response?.data?.error || 'Régénération impossible.' });
    }
  };

  const handleResetMfa = async () => {
    setMfaMessage(null);
    try {
      const updated = await resetUserMfa(user.id);
      setMfa({ enabled: updated.mfaEnabled, enabledAt: updated.mfaEnabledAt });
      setConfirmMfaReset(false);
      setMfaMessage({ ok: true, text: 'MFA réinitialisé ✔ — nouvel enrôlement à la prochaine connexion.' });
      onSaved();
    } catch (err) {
      setMfaMessage({ ok: false, text: err.response?.data?.error || 'Réinitialisation impossible.' });
    }
  };

  // Ajoute l'adresse du dernier appel du robot à sa liste (aide au réglage)
  const addLastCallIp = () => {
    const lines = form.allowedIps.split('\n').map(l => l.trim()).filter(Boolean);
    if (!lines.includes(user.lastCallIp)) {
      setForm({ ...form, allowedIps: [...lines, user.lastCallIp].join('\n') });
    }
  };

  const handleDelete = async () => {
    setDeleteError('');
    try {
      await deleteUser(user.id, deleteConfirm);
      onSaved();
      onClose();
    } catch (err) {
      setDeleteError(err.response?.data?.error || 'Suppression impossible.');
    }
  };

  // Clé générée : écran dédié, la clé n'est plus jamais réaffichée ensuite
  if (shownKey) {
    return (
      <Modal title={`Clé du robot ${form.username} ✔`} titleColor="#1a7f37" onClose={onClose}
             footer={<Button variant="primary" onClick={onClose}>J'ai copié la clé</Button>}>
        <KeyReveal value={shownKey} />
      </Modal>
    );
  }

  return (
    <Modal title={isNew ? 'Nouvel utilisateur' : `Utilisateur ${user.username}`} onClose={onClose}
           subtitle={isNew ? null
             : `Créé le ${formatDateTime(user.createdAt)} · dernière connexion ${formatDateTime(user.lastLoginAt)}`}>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gap: '0.75rem', gridTemplateColumns: '1fr 1fr' }}>
            <div>
              <label style={label}>Identifiant</label>
              <input style={{ ...input, ...(isNew ? {} : { color: '#8a8f99' }) }} value={form.username}
                     onChange={set('username')} readOnly={!isNew} required />
            </div>
            <div>
              <label style={label}>Rôle</label>
              <select style={input} value={form.role} onChange={set('role')}
                      disabled={isMe || roleOptions.length === 1}
                      title={isMe ? 'Vous ne pouvez pas modifier votre propre rôle' : ''}>
                {roleOptions.map(value => <option key={value} value={value}>{ROLES[value][0]}</option>)}
              </select>
            </div>
            {!isRobot && (
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={label}>Email</label>
                <input style={input} type="email" value={form.email} onChange={set('email')} />
              </div>
            )}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={label}>Notes</label>
              <textarea style={{ ...input, minHeight: '80px', resize: 'vertical' }} maxLength={2000}
                        value={form.notes} onChange={set('notes')} />
            </div>
            {isRobot && (
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={label}>Adresses IP autorisées — IP ou plage CIDR, une par ligne</label>
                <textarea style={{ ...input, minHeight: '70px', resize: 'vertical', fontFamily: 'monospace', fontSize: '0.85rem' }}
                          value={form.allowedIps} onChange={set('allowedIps')}
                          placeholder={'10.200.200.15\n10.0.0.0/8'} />
                <div style={hint}>
                  Vide = liste par défaut ({policy?.robotAllowedIps?.length
                    ? <code>{policy.robotAllowedIps.join(', ')}</code>
                    : 'aucune restriction'}, variable ROBOT_ALLOWED_IPS).
                  {!isNew && user.lastCallIp && (
                    <> Dernier appel depuis <code>{user.lastCallIp}</code>
                      {user.lastCallStatus >= 400 && <span style={{ color: '#b3261e' }}> (refusé)</span>}
                      {' '}<button type="button" style={linkInline} onClick={addLastCallIp}>ajouter</button>
                    </>
                  )}
                </div>
              </div>
            )}
            {isNew && !isRobot && (
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={label}>Mot de passe (8 caractères min.)</label>
                <PasswordInput style={input} value={form.password} onChange={set('password')} required />
              </div>
            )}
          </div>
          {isRobot && (
            <p style={{ color: '#955d00', fontSize: '0.8rem', margin: '0.75rem 0 0' }}>
              Compte robot : accès packages + instances en lecture ; chaque appel est journalisé
              (bouton « Activité »), journaux conservés 30 jours.
              {isNew && ' La clé d\'accès est générée à la création et affichée une seule fois.'}
            </p>
          )}
          {error && <div style={errorStyle}>{error}</div>}
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem', justifyContent: 'flex-end' }}>
            <Button onClick={onClose}>Annuler</Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? 'Enregistrement...' : isNew ? (isRobot ? 'Créer et générer la clé' : 'Créer') : 'Enregistrer'}
            </Button>
          </div>
        </form>

        {!isNew && !isRobot && (
          <div style={section}>
            <label style={label}>Réinitialiser le mot de passe (8 caractères min.)</label>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <PasswordInput style={input} wrapperStyle={{ flex: 1 }} value={newPassword}
                             onChange={e => setNewPassword(e.target.value)} autoComplete="new-password" />
              <Button onClick={handleResetPassword}>Réinitialiser</Button>
            </div>
            {pwdMessage && <div style={pwdMessage.ok ? successStyle : errorStyle}>{pwdMessage.text}</div>}
          </div>
        )}

        {!isNew && !isRobot && (
          <div style={section}>
            <label style={label}>Double authentification (MFA)</label>
            {confirmMfaReset ? (
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ color: '#b3261e', fontSize: '0.85rem', flex: 1 }}>
                  L'application d'authentification actuelle ne sera plus acceptée ; ses navigateurs de
                  confiance sont oubliés.
                </span>
                <Button size="sm" onClick={() => setConfirmMfaReset(false)}>Annuler</Button>
                <Button size="sm" variant="danger" onClick={handleResetMfa}>Confirmer</Button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <span style={{ color: mfa.enabled ? '#1a7f37' : '#955d00', fontSize: '0.85rem', flex: 1 }}>
                  {mfa.enabled
                    ? `Activé le ${formatDateTime(mfa.enabledAt)}`
                    : policy?.mfaRequired === false
                      ? 'Non activé (MFA non exigé sur ce serveur)'
                      : 'Non activé — enrôlement demandé à la prochaine connexion'}
                </span>
                {mfa.enabled && <Button onClick={() => setConfirmMfaReset(true)}>Réinitialiser le MFA</Button>}
              </div>
            )}
            {mfaMessage && <div style={mfaMessage.ok ? successStyle : errorStyle}>{mfaMessage.text}</div>}
          </div>
        )}

        {!isNew && isRobot && (
          <div style={section}>
            <label style={label}>Clé d'accès</label>
            {confirmRegen ? (
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ color: '#b3261e', fontSize: '0.85rem', flex: 1 }}>
                  L'ancienne clé cessera de fonctionner immédiatement.
                </span>
                <Button size="sm" onClick={() => setConfirmRegen(false)}>Annuler</Button>
                <Button size="sm" variant="danger" onClick={handleRegenerate}>Confirmer</Button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <span style={{ color: '#8a8f99', fontSize: '0.85rem', flex: 1 }}>
                  Non réaffichable (seule une empreinte est stockée).
                </span>
                <Button onClick={() => setConfirmRegen(true)}>Régénérer la clé</Button>
              </div>
            )}
            {pwdMessage && <div style={errorStyle}>{pwdMessage.text}</div>}
          </div>
        )}

        {!isNew && !isMe && (
          <div style={section}>
            <label style={{ ...label, color: '#b3261e' }}>
              Supprimer l'utilisateur — saisir <b>{user.username}</b> pour confirmer
            </label>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input style={{ ...input, flex: 1 }} value={deleteConfirm} placeholder={user.username}
                     onChange={e => setDeleteConfirm(e.target.value)} autoComplete="off" />
              <Button variant="danger" onClick={handleDelete} disabled={deleteConfirm !== user.username}>
                Supprimer
              </Button>
            </div>
            <div style={{ color: '#8a8f99', fontSize: '0.78rem', marginTop: '0.35rem' }}>
              {isRobot
                ? 'Définitif : le compte et ses journaux (connexion, activité) sont supprimés.'
                : 'Définitif. Son journal de connexion est conservé (sans lien vers le compte).'}
            </div>
            {deleteError && <div style={errorStyle}>{deleteError}</div>}
          </div>
        )}
    </Modal>
  );
}

/**
 * Affichage unique de la clé + copie. navigator.clipboard n'existe qu'en contexte
 * sécurisé (https / localhost) : repli sur la sélection + execCommand('copy').
 */
function KeyReveal({ value }) {
  const ref = useRef(null);
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) await navigator.clipboard.writeText(value);
      else { ref.current.select(); document.execCommand('copy'); }
      setCopied(true);
    } catch {
      ref.current.select();
    }
  };

  return (
    <>
      <p style={{ color: '#b3261e', fontSize: '0.85rem', margin: '0 0 0.75rem' }}>
        Copiez cette clé maintenant : elle ne sera <b>plus jamais affichée</b>.
      </p>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <input ref={ref} readOnly value={value} onFocus={e => e.target.select()}
               style={{ ...input, fontFamily: 'monospace', fontSize: '0.85rem' }} />
        <Button onClick={copy}>{copied ? 'Copiée ✔' : 'Copier'}</Button>
      </div>
      <p style={{ color: '#6b7280', fontSize: '0.8rem', margin: '0.75rem 0 0' }}>
        Utilisation : en-tête <code>X-Agent-Key: &lt;clé&gt;</code> sur chaque appel, ou connexion
        classique (<code>/api/auth/login</code>) avec l'identifiant et la clé comme mot de passe.
      </p>
    </>
  );
}

const section = { borderTop: '1px solid #ececec', marginTop: '1.5rem', paddingTop: '1rem' };
const hint = { color: '#8a8f99', fontSize: '0.78rem', marginTop: '0.35rem', lineHeight: 1.45 };
const linkInline = { background: 'none', border: 'none', padding: 0, color: '#005cc5', cursor: 'pointer', font: 'inherit', textDecoration: 'underline' };
